// RepurposingQuellen — DFY-tier settings page (slice 7, R8). The company curates the YouTube SOURCE
// channels Lina picks 1–2 recent videos from each week. Server enforces tier (404 if not dfy) + the max-5
// cap; the route is DFY-gated in App.tsx and the sidebar item is hidden for non-DFY tenants. Resolution
// (handle→channel) runs async server-side, so a freshly-added channel shows "wird geprüft…" until
// resolved or failed.
import { useState } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { apiRequest } from "@/lib/queryClient"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Skeleton } from "@/components/ui/skeleton"
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { useToast } from "@/hooks/use-toast"
import { Loader2, Youtube, Trash2, Plus, CheckCircle2, AlertCircle, Clock } from "lucide-react"

const QUERY_KEY = ["/api/dfy/repurpose-sources"] as const

type Source = {
  id: string
  channelInput: string
  channelId: string | null
  channelTitle: string | null
  resolutionStatus: "pending" | "resolved" | "failed"
  resolutionError: string | null
}
type SourcesResponse = { sources: Source[]; max: number }

function StatusBadge({ s }: { s: Source }) {
  if (s.resolutionStatus === "resolved") {
    return <Badge className="gap-1 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"><CheckCircle2 className="h-3 w-3" /> Aktiv</Badge>
  }
  if (s.resolutionStatus === "failed") {
    return <Badge className="gap-1 bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"><AlertCircle className="h-3 w-3" /> Nicht gefunden</Badge>
  }
  return <Badge variant="outline" className="gap-1"><Clock className="h-3 w-3 animate-pulse" /> Wird geprüft…</Badge>
}

// Client-side pre-validation mirrors the server allowlist (repurposeChannel.ts) so an obvious
// bad paste gets an instant German hint instead of a server round-trip. The server remains the
// source of truth — this only catches the common cases.
function validateChannelInput(raw: string): string | null {
  const input = raw.trim()
  if (!input) return "Bitte geben Sie einen Kanal ein."
  if (input.length > 256) return "Die Eingabe ist zu lang."
  if (input.startsWith("@")) {
    return /^[A-Za-z0-9._-]{1,100}$/.test(input.slice(1))
      ? null
      : "Ungültiger @handle. Erlaubt sind Buchstaben, Ziffern sowie . _ -"
  }
  let u: URL
  try {
    u = new URL(input)
  } catch {
    return "Geben Sie einen @handle oder eine youtube.com/@handle- bzw. /channel/UC…-URL ein."
  }
  if (!u.protocol.startsWith("http")) return "Nur http(s)-URLs werden unterstützt."
  const host = u.hostname.toLowerCase().replace(/\.$/, "")
  const isYouTube = host === "youtube.com" || host.endsWith(".youtube.com") || host === "youtu.be"
  if (!isYouTube) return "Das ist keine YouTube-Kanal-URL."
  if (u.pathname.split("/").filter(Boolean)[0] === "c") {
    return "Das ist eine veraltete /c/-URL. Bitte fügen Sie den @handle oder die /channel/UC…-URL ein."
  }
  return null
}

export default function RepurposingQuellen() {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [input, setInput] = useState("")

  const { data, isLoading, error, refetch } = useQuery<SourcesResponse>({
    queryKey: QUERY_KEY,
    queryFn: () => apiRequest("GET", "/api/dfy/repurpose-sources"),
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    // Poll while any channel is still resolving so the badge flips without a manual reload.
    refetchInterval: (q) => (q.state.data?.sources?.some((s) => s.resolutionStatus === "pending") ? 4000 : false),
  })

  const refresh = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY })

  const addMutation = useMutation({
    mutationFn: (value: string) => apiRequest("POST", "/api/dfy/repurpose-sources", { input: value }),
    onSuccess: () => {
      setInput("")
      refresh()
      toast({ title: "Kanal hinzugefügt", description: "Wird im Hintergrund geprüft." })
    },
    onError: (err: any) => {
      toast({ title: "Konnte nicht hinzugefügt werden", description: err?.data?.error || err?.message || "", variant: "destructive" })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/dfy/repurpose-sources/${id}`),
    onSuccess: () => {
      refresh()
      toast({ title: "Kanal entfernt" })
    },
    onError: (err: any) => {
      toast({ title: "Entfernen fehlgeschlagen", description: err?.data?.error || err?.message || "", variant: "destructive" })
    },
  })

  const sources = data?.sources ?? []
  const max = data?.max ?? 5
  // A live slot is a pending|resolved channel; failed rows don't consume a slot (mirrors the server cap).
  const liveCount = sources.filter((s) => s.resolutionStatus !== "failed").length
  const atCap = liveCount >= max
  const canSubmit = input.trim().length > 0 && !atCap && !addMutation.isPending

  const submit = () => {
    if (!canSubmit) return
    const validationError = validateChannelInput(input)
    if (validationError) {
      toast({ title: "Ungültige Eingabe", description: validationError, variant: "destructive" })
      return
    }
    addMutation.mutate(input.trim())
  }

  return (
    <div className="container mx-auto p-6 space-y-6 max-w-3xl">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2" data-testid="heading-repurposing-quellen">
          <Youtube className="h-8 w-8 text-red-600" /> Repurposing-Quellen
        </h1>
        <p className="text-muted-foreground mt-2">
          Lina sucht jede Woche ein bis zwei aktuelle Videos aus diesen YouTube-Kanälen aus und macht daraus
          LinkedIn-Beiträge für Ihren Wochenplan. Sie geben hier die Kanäle vor, auswählen und texten
          übernimmt Lina.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Kanal hinzufügen</CardTitle>
          <CardDescription>
            Fügen Sie einen <strong>@handle</strong> oder eine Kanal-URL ein (z. B. <code>@firmenname</code> oder{" "}
            <code>youtube.com/channel/UC…</code>). Maximal {max} Kanäle.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            className="flex gap-2"
            onSubmit={(e) => { e.preventDefault(); submit() }}
          >
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="@kanalname oder youtube.com/@kanalname"
              disabled={atCap}
              aria-label="YouTube-Kanal"
              data-testid="input-channel"
            />
            <Button type="submit" disabled={!canSubmit} data-testid="button-add-channel">
              {addMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              <span className="ml-1">Hinzufügen</span>
            </Button>
          </form>
          {atCap && (
            <p className="text-sm text-muted-foreground mt-2">
              Maximum erreicht ({max}). Entferne einen Kanal, um einen neuen hinzuzufügen.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Ihre Kanäle {sources.length > 0 && `(${liveCount}/${max} aktiv)`}</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3" data-testid="skeleton-channels">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-12 rounded-lg" />
              ))}
            </div>
          ) : error ? (
            <div className="py-8 text-center space-y-3">
              <p className="text-destructive">Die Kanäle konnten nicht geladen werden. Bitte versuchen Sie es später erneut.</p>
              <Button variant="outline" size="sm" onClick={() => refetch()} data-testid="button-retry">
                Erneut versuchen
              </Button>
            </div>
          ) : sources.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground">
              <Youtube className="h-8 w-8 mx-auto mb-3 opacity-50" />
              <p>Noch keine Kanäle. Fügen Sie oben den ersten hinzu.</p>
            </div>
          ) : (
            <ul className="divide-y">
              {sources.map((s) => (
                <li key={s.id} className="flex items-center justify-between gap-3 py-3" data-testid={`row-channel-${s.id}`}>
                  <div className="min-w-0">
                    <div className="font-medium truncate">{s.channelTitle || s.channelInput}</div>
                    <div className="text-sm text-muted-foreground truncate">
                      {s.channelTitle ? s.channelInput : null}
                      {/* Friendly, bounded German note for failed rows — the raw resolutionError
                          (which can carry technical n8n text) is demoted to a hover tooltip. */}
                      {s.resolutionStatus === "failed" && (
                        <span title={s.resolutionError ?? undefined}>
                          {s.channelTitle ? ": " : ""}Kanal konnte nicht gefunden werden. Prüfen Sie den @handle oder die URL.
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <StatusBadge s={s} />
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" aria-label="Kanal entfernen" data-testid={`button-delete-${s.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Kanal entfernen?</AlertDialogTitle>
                          <AlertDialogDescription>
                            {s.channelTitle || s.channelInput} wird nicht mehr für neue Wochenpläne verwendet.
                            Bereits erstellte Beiträge bleiben erhalten.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => deleteMutation.mutate(s.id)}
                            disabled={deleteMutation.isPending}
                            data-testid={`button-confirm-delete-${s.id}`}
                          >
                            {deleteMutation.isPending ? "Entfernen…" : "Entfernen"}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
